import { EconomicEventOutput, SnakeMeta } from '@api';
import { ButtonLink, Icon, WithNAFallback } from '@design';
import { getPriceStatus, PriceStatus } from '@helpers';
import { useChartNavigate } from '@terminal/hooks/use-chart-navigate/useChartNavigate';
import { useSnakeMeta } from '@terminal/react-query-hooks/snakes/useSnake';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { RunScenario } from '../run-scenario/RunScenario';
import { SummaryTable } from '../summary-table/SummaryTable';
import { getConditionOptions } from '../utils/getConditionOptions';
import { formatNumber } from '../utils/number-formatter';
import styles from './EconomicsEventDrawerContent.module.scss';

const surpriseClassMap: Record<PriceStatus, string | undefined> = {
  positive: styles.positive,
  negative: styles.negative,
  default: undefined,
};

export interface EconomicsEventDrawerContentProps {
  data: EconomicEventOutput;
}

export const EconomicsEventDrawerContent = ({
  data,
}: EconomicsEventDrawerContentProps) => {
  const { t } = useTranslation(['calendar', 'chart']);
  const chartNavigate = useChartNavigate();
  const { trackChartOpen } = usePosthogEvents();

  const {
    category,
    period,
    period_year,
    description,
    consensus,
    consensus_unit,
    actual,
    actual_unit: actualUnit,
    surprise,
    surprise_unit,
    name,
    snakes,
  } = data;

  const snakeName = snakes?.actual;
  const { data: snakeData, isFetching: isFetchingSnakeData } =
    useSnakeMeta(snakeName);

  const isBooleanEvent = !!snakes?.event;
  const actualUnitHeader = actualUnit ? [`(${actualUnit})`] : [''];

  const tableHeaders = [
    ...actualUnitHeader,
    t('calendar:expected'),
    t('calendar:reported'),
    t('calendar:surprise'),
  ];

  const getSurpriseClass = (value: number | null | undefined) =>
    clsx(
      styles.surpriseWrapper,
      surpriseClassMap[getPriceStatus(value ?? undefined)]
    );

  const tableRows = [
    {
      fieldName: name,
      consensus: (
        <WithNAFallback
          value={
            consensus !== undefined &&
            consensus !== null &&
            formatNumber({
              isPercentage: consensus_unit === '%',
              value: consensus,
            })
          }
        />
      ),
      reported: (
        <WithNAFallback
          value={
            actual !== undefined &&
            actual !== null &&
            formatNumber({ isPercentage: actualUnit === '%', value: actual })
          }
        />
      ),
      surprise: (
        <div className={getSurpriseClass(surprise)}>
          <WithNAFallback
            value={
              surprise !== undefined &&
              surprise !== null &&
              formatNumber({
                isPercentage: (surprise_unit ?? actualUnit) === '%',
                value: surprise,
              })
            }
          />
        </div>
      ),
    },
  ];

  const formattedPeriodLabel = period
    ? `${period}, ${period_year}`
    : period_year;
  const dateTitleLabel = `${category} ${formattedPeriodLabel}`;

  const conditionOptions = getConditionOptions({ t, data });

  const onClick = (snake: SnakeMeta) =>
    chartNavigate([[snake.entity, snake.snake, 0]]);

  return (
    <div className={styles.container}>
      <div className={styles.dateTitleWrapper} data-testid="title-label">
        {dateTitleLabel}
      </div>
      {description && (
        <div
          className={styles.descriptionWrapper}
          data-testid="description-label"
        >
          {description}
        </div>
      )}
      <SummaryTable
        title={
          <div className={styles.styledTitleWrapper}>
            <div className={styles.titleWrapper}>{t('calendar:summary')}</div>
            {!!snakeData && !isFetchingSnakeData && (
              <ButtonLink
                data-testid="open-in-chart"
                label={
                  <>
                    {t('chart:openIn')}
                    <Icon iconName={'ChartMixed'} size={16} />
                    {t('chart:title')}
                  </>
                }
                onClick={() => {
                  trackChartOpen('events');
                  onClick(snakeData);
                }}
              />
            )}
          </div>
        }
        tableHeaders={tableHeaders}
        tableRows={tableRows}
      />
      {!isBooleanEvent && !!conditionOptions.length && (
        <RunScenario data={data} conditionOptions={conditionOptions} />
      )}
    </div>
  );
};
